import useAnimation from './useAnimation';
import useAppTheme from './useAppTheme';
import useColor, {BackgroundStyleKey, BorderStyleKey} from './useColor';
import useTypography from './useTypography';

import {css, SerializedStyles} from '@emotion/react';

type ButtonSizeFactoryArgs = {
  backgroundStyle?: BackgroundStyleKey;
  borderStyle?: BorderStyleKey;
  styles?: SerializedStyles;
};

export const BUTTON_SIZES = ['l', 'm'] as const;
export type ButtonSizeKey = (typeof BUTTON_SIZES)[number];
export type ButtonSizes = Record<ButtonSizeKey, SerializedStyles>;

export const BUTTON_STYLES = [
  'primary',
  'accent',
  'secondary',
  'secondarySubdued',
  'secondaryWhite',
  'gradient',
] as const;
export type ButtonStyleKey = (typeof BUTTON_STYLES)[number];
export type ButtonStyles = Record<ButtonStyleKey, ButtonSizes>;

export const PAGE_STYLES = ['container'] as const;
export type PageStyleKey = (typeof PAGE_STYLES)[number];
export type PageStyles = Record<PageStyleKey, SerializedStyles>;

export const INPUT_STYLES = ['default', 'error'] as const;
export type InputStyleKey = (typeof INPUT_STYLES)[number];
export type InputStyles = Record<InputStyleKey, SerializedStyles>;

type UseComponent = {
  buttons: ButtonStyles;
  input: InputStyles;
  page: PageStyles;
};

/**
 * Custom hook providing reusable styles for components.
 *
 * @returns {UseComponent} Object containing button and page styles.
 * @property {ButtonStyles} buttons - Styles for different button types.
 * @property {PageStyles} page - Styles for page containers.
 * @property {InputStyles} input - Styles for inputs.
 */
const useComponent = () => {
  const {breakpoints, colors, font, screens, shadows, spacings} = useAppTheme();
  const {background, border, pallet} = useColor();
  const {background: bgAnimation} = useAnimation();
  const {body} = useTypography();

  const buttonSizeFactory = ({
    backgroundStyle,
    borderStyle,
    styles,
  }: ButtonSizeFactoryArgs) => {
    const button = css([
      styles,
      {
        transition: 'all 0.15s ease',
        fontWeight: font.weight.bold,
        padding: `0 ${spacings.px24}`,
        borderRadius: spacings.px20,
        width: 'fit-content',
        display: 'flex',
        alignItems: 'center',
        '&:disabled': {
          color: pallet.content.neutral.disabled,
          cursor: 'default',
        },
        boxShadow: shadows.m,
        textWrap: 'nowrap',
        justifyContent: 'center',
      },
      backgroundStyle && background[backgroundStyle],
      borderStyle && border[borderStyle],
    ]);

    const buttonL = css({
      height: spacings.px40,
      fontSize: spacings.px13,
    });

    const buttonM = css({
      height: spacings.px32,
      fontSize: spacings.px12,
    });

    return {
      /** 40px */
      l: css([button, buttonL]),
      /** 32px */
      m: css([button, buttonM]),
    };
  };

  // Button
  const buttons = {
    primary: buttonSizeFactory({
      backgroundStyle: 'strong',
      styles: css({color: pallet.content.white.defaultColor}),
    }),
    accent: buttonSizeFactory({
      backgroundStyle: 'accent',
      styles: css({color: pallet.content.white.defaultColor}),
    }),
    secondary: buttonSizeFactory({
      backgroundStyle: 'white',
      borderStyle: 'neutral',
      styles: css({borderWidth: spacings.px2, borderStyle: 'solid'}),
    }),
    secondarySubdued: buttonSizeFactory({
      backgroundStyle: 'white',
      borderStyle: 'light',
      styles: css({
        color: pallet.content.neutral.defaultColor,
        borderWidth: spacings.px2,
        borderStyle: 'solid',
        '&:active': {backgroundColor: pallet.background.white.defaultColor},
        '&:disabled': {backgroundColor: pallet.background.white.defaultColor},
      }),
    }),
    secondaryWhite: buttonSizeFactory({
      styles: css({
        color: pallet.content.white.defaultColor,
        borderWidth: spacings.px2,
        borderStyle: 'solid',
        borderColor: colors.white,
        '&:hover': {opacity: 0.8},
        '&:disabled': {opacity: 0.3},
      }),
    }),
    gradient: buttonSizeFactory({
      styles: css({
        backgroundImage: pallet.background.gradient.defaultColor,
        color: pallet.content.white.defaultColor,
        animation: `${bgAnimation.position} 10s infinite`,
        backgroundSize: '200% 100%',
      }),
    }),
  } as const satisfies ButtonStyles;

  // Page
  const page = {
    container: css({
      width: '100%',
      maxWidth: screens.xxl,
      paddingLeft: spacings.px20,
      paddingRight: spacings.px20,
      [breakpoints.lg]: {
        paddingLeft: spacings.px86,
        paddingRight: spacings.px86,
      },
    }),
  } satisfies PageStyles;

  const inputCss = css([
    body.s,
    {
      borderWidth: spacings.px2,
      borderRadius: spacings.px6,
      padding: `${spacings.px4} ${spacings.px12}`,
    },
  ]);

  // Input
  const input = {
    default: css([
      border.light,
      inputCss,
      {
        '&:focus': {borderColor: pallet.border.light.active, outline: 0},
        '&:disabled': {backgroundColor: pallet.background.neutral.disabled},
      },
    ]),
    error: css([
      inputCss,
      {
        borderColor: pallet.border.negative.defaultColor,
        '&:focus': {outline: 0},
        '&:disabled': {backgroundColor: pallet.background.neutral.disabled},
      },
    ]),
  } satisfies InputStyles;

  return {buttons, page, input} satisfies UseComponent;
};

export default useComponent;
